"use client";

import Image from "next/image";
import { useState, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight, X, Images } from "lucide-react";
import { useTranslations } from "next-intl";
import { Noticia } from "@/lib/api/datosGenerales/types/noticias";
import NoticiaShell from "./NoticiaShell";

interface NoticiaPlantillaCarruselProps {
  noticia: Noticia;
  locale: "es" | "en";
  tiempoLectura: number;
  noticiasRelacionadas: Noticia[];
  basePath?: string;
  imagenes?: string[];
}

function Carrusel({
  imagenes,
  alt,
}: {
  imagenes: string[];
  alt: string;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const nextSlide = useCallback(
    () => setCurrentIndex((prev) => (prev + 1) % imagenes.length),
    [imagenes.length],
  );
  const prevSlide = useCallback(
    () =>
      setCurrentIndex((prev) => (prev - 1 + imagenes.length) % imagenes.length),
    [imagenes.length],
  );

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setIsLightboxOpen(true);
    setIsAutoPlaying(false);
  };
  const closeLightbox = () => setIsLightboxOpen(false);
  const prevLightbox = useCallback(
    () =>
      setLightboxIndex((prev) => (prev - 1 + imagenes.length) % imagenes.length),
    [imagenes.length],
  );
  const nextLightbox = useCallback(
    () => setLightboxIndex((prev) => (prev + 1) % imagenes.length),
    [imagenes.length],
  );

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  useEffect(() => {
    if (!isLightboxOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextLightbox();
      if (e.key === "ArrowLeft") prevLightbox();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isLightboxOpen, nextLightbox, prevLightbox]);

  return (
    <>
      <div
        className="relative aspect-video rounded-2xl overflow-hidden group bg-gray-100 border border-gray-200"
        onMouseEnter={() => setIsAutoPlaying(false)}
        onMouseLeave={() => setIsAutoPlaying(true)}
      >
        {imagenes.map((img, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === currentIndex ? "opacity-100" : "opacity-0"
            }`}
          >
            <Image
              src={img}
              alt={`${alt} - ${index + 1}`}
              fill
              sizes="(max-width: 896px) 100vw, 896px"
              className="object-cover cursor-pointer"
              onClick={() => openLightbox(index)}
              priority={index === 0}
            />
          </div>
        ))}

        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 text-[#102542]"
          aria-label="Previous"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 text-[#102542]"
          aria-label="Next"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
          {imagenes.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentIndex
                  ? "w-6 h-1.5 bg-white"
                  : "w-1.5 h-1.5 bg-white/60 hover:bg-white/80"
              }`}
              aria-label={`Slide ${index + 1}`}
            />
          ))}
        </div>

        <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-black/40 backdrop-blur text-white text-xs font-mono tabular-nums">
          {currentIndex + 1} / {imagenes.length}
        </div>
      </div>

      {/* Thumbnails */}
      <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
        {imagenes.map((img, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`relative shrink-0 w-20 h-14 md:w-24 md:h-16 rounded-lg overflow-hidden transition-all border ${
              index === currentIndex
                ? "border-[#102542]"
                : "border-gray-200 opacity-60 hover:opacity-100"
            }`}
          >
            <Image
              src={img}
              alt={`Thumb ${index + 1}`}
              fill
              sizes="96px"
              className="object-cover"
            />
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {isLightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevLightbox();
            }}
            className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
            aria-label="Previous"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextLightbox();
            }}
            className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
            aria-label="Next"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
          <div
            className="relative w-full h-full max-w-6xl max-h-[85vh] mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <Image
              src={imagenes[lightboxIndex]}
              alt={`${alt} - ${lightboxIndex + 1}`}
              fill
              sizes="(max-width: 1152px) 100vw, 1152px"
              className="object-contain"
            />
          </div>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/10 text-white text-xs font-mono tabular-nums">
            {lightboxIndex + 1} / {imagenes.length}
          </div>
        </div>
      )}
    </>
  );
}

export default function NoticiaPlantillaCarrusel({
  imagenes,
  ...rest
}: NoticiaPlantillaCarruselProps) {
  const t = useTranslations("noticiasPage.detail");
  const hasImages = imagenes && imagenes.length > 0;

  return (
    <NoticiaShell
      {...rest}
      extraMeta={
        hasImages ? (
          <span className="inline-flex items-center gap-1.5">
            <Images className="w-3 h-3" />
            {t("templateImages", { count: imagenes.length })}
          </span>
        ) : null
      }
      topMedia={
        hasImages ? (
          <Carrusel imagenes={imagenes} alt={rest.noticia.titulo} />
        ) : null
      }
    />
  );
}
